import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { STELLA_REPLIES, QUICK_CHIPS } from "@/data/content";

const StellaAvatar = ({ size = 40 }) => (
  <span
    className="relative grid shrink-0 place-items-center overflow-hidden rounded-full ring-1 ring-gold/40"
    style={{ width: size, height: size }}
  >
    <span className="absolute inset-0 bg-gradient-to-br from-gold-light via-gold to-gold-deep" />
    <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.6),transparent_55%)]" />
    <span className="relative font-heading text-sm font-black text-ink">S</span>
  </span>
);

const VoiceWave = () => (
  <span className="flex items-end gap-0.5" aria-hidden="true">
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className="w-0.5 rounded-full bg-gold animate-wave"
        style={{ height: 12, animationDelay: `${i * 0.12}s` }}
      />
    ))}
  </span>
);

const TypingDots = () => (
  <span className="flex items-center gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/50" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </span>
);

export const StellaChat = () => {
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { from: "stella", text: STELLA_REPLIES.default },
  ]);
  const scrollRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const pushStella = (text) => {
    setTyping(true);
    timeoutRef.current = setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { from: "stella", text }]);
    }, 900);
  };

  const resolveReply = (text, key) => {
    if (key && STELLA_REPLIES[key]) return STELLA_REPLIES[key];
    const t = text.toLowerCase();
    if (t.includes("price") || t.includes("cost") || t.includes("pricing")) return STELLA_REPLIES.pricing;
    if (t.includes("app")) return STELLA_REPLIES.app;
    if (t.includes("website") || t.includes("web")) return STELLA_REPLIES.website;
    if (t.includes("book") || t.includes("consult") || t.includes("meeting")) return STELLA_REPLIES.consultation;
    if (t.includes("sales") || t.includes("talk") || t.includes("contact")) return STELLA_REPLIES.sales;
    return "Great question! For anything specific, the fastest path is a free consultation — head to our Contact page and I'll make sure the right expert reaches out.";
  };

  const send = (text, key) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setMessages((m) => [...m, { from: "user", text: trimmed }]);
    setInput("");
    pushStella(resolveReply(trimmed, key));
  };

  return (
    <>
      {/* Launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setOpen(true)}
            data-testid="stella-launcher"
            aria-label="Chat with Stella George"
            className="fixed bottom-6 right-6 z-[55] flex items-center gap-3 rounded-full bg-gold p-1.5 pr-5 text-ink shadow-[0_10px_40px_-8px_rgba(212,175,55,0.6)]"
          >
            <StellaAvatar size={44} />
            <span className="text-left">
              <span className="block text-[11px] font-semibold leading-tight">Ask Stella</span>
              <span className="block text-[10px] leading-tight opacity-70">AI Assistant · Online</span>
            </span>
            <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-gold" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            data-testid="stella-panel"
            className="glass-strong fixed bottom-6 right-4 z-[56] flex h-[70vh] max-h-[560px] w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl border border-white/10 shadow-2xl sm:right-6"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center gap-3">
                <StellaAvatar size={42} />
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold text-white">
                    Stella George <Sparkles className="h-3.5 w-3.5 text-gold" />
                  </p>
                  <p className="flex items-center gap-2 text-[11px] text-white/50">
                    AI Business Assistant <VoiceWave />
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-white/70 hover:text-white"
                aria-label="Close chat"
                data-testid="stella-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="stella-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex items-end gap-2 ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  {m.from === "stella" && <StellaAvatar size={28} />}
                  <div
                    className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.from === "user"
                        ? "rounded-br-sm bg-gold text-ink"
                        : "rounded-bl-sm bg-white/[0.06] text-white/90"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {typing && (
                <div className="flex items-end gap-2">
                  <StellaAvatar size={28} />
                  <div className="rounded-2xl rounded-bl-sm bg-white/[0.06] px-4 py-2">
                    <TypingDots />
                  </div>
                </div>
              )}
            </div>

            {/* Quick chips */}
            <div className="flex flex-wrap gap-2 px-4 pb-3">
              {QUICK_CHIPS.map((chip) => (
                <button
                  key={chip.key}
                  onClick={() => send(chip.label, chip.key)}
                  data-testid={`stella-chip-${chip.key}`}
                  className="rounded-full border border-gold/30 bg-gold/5 px-3 py-1.5 text-[11px] text-gold transition-colors hover:bg-gold/15"
                >
                  {chip.label}
                </button>
              ))}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="flex items-center gap-2 border-t border-white/10 bg-white/[0.03] p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Stella anything…"
                data-testid="stella-input"
                className="w-full bg-transparent px-2 text-sm text-white placeholder:text-white/40 focus:outline-none"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                data-testid="stella-send"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gold text-ink transition-transform hover:scale-105 disabled:opacity-50"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
